/**
 * Mailvelope — OpenPGP email encryption
 *
 * Declares one web-accessible-resource group matched to every http(s) origin with no
 * `use_dynamic_url`. It holds the client API script, several component HTML pages
 * (`components/editor/editor.html`, `components/generate-key/genKey.html`, ...), a set
 * of vendor-named PNGs and `main.css`. All resolve from any page at a fixed URL.
 *
 * The lock/sign mail icons are the cleanest signal: vendor-named images loaded via
 * `probeImage` with no CORS grant, served only when the extension is installed.
 *
 * Manifest verified against version 6.3.0.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kajibbejlbohfaggdiogboambcijhkke';

export const mailvelope: ExtensionDefinition = {
  id: ID,
  name: 'Mailvelope - Secure your email with PGP',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'One static WAR group on every http(s) origin, no dynamic URL, including named ' +
    'mail icons and component HTML. Detection is conclusive and CORS-free.',
  signals: [
    {
      id: 'war-mail-locked-image',
      kind: 'web-accessible-resource',
      description:
        'Load img/mail_locked_96.png via <img> — a vendor-named icon exposed to every ' +
        'origin; needs no CORS grant, so it survives a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'img/mail_locked_96.png'),
    },
    {
      id: 'war-editor-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch components/editor/editor.html — the encryption editor page, exposed to ' +
        'every origin.',
      weight: 0.5,
      run: () => probeFetch(ID, 'components/editor/editor.html'),
    },
    {
      id: 'war-main-css',
      kind: 'web-accessible-resource',
      description: 'Fetch main.css — a third independent path corroborating the match.',
      weight: 0.5,
      run: () => probeFetch(ID, 'main.css'),
    },
  ],
};
